import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getDeviceToken } from "../utils/deviceToken";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: token preferenziale da sessionStorage (tab di impersonation) o
// localStorage (sessione normale). Permette di avere 2 sessioni separate
// nelle 2 tab dello stesso browser (es. SuperAdmin in tab A, impersonation
// in tab B).
const getStoredToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");
const isImpersonationTab = () => sessionStorage.getItem("is_impersonation_tab") === "1";
const setStoredToken = (t) => {
  if (isImpersonationTab()) sessionStorage.setItem("token", t);
  else localStorage.setItem("token", t);
};
const clearStoredToken = () => {
  if (isImpersonationTab()) {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("is_impersonation_tab");
  }
  localStorage.removeItem("token");
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [locali, setLocali] = useState([]);
  const [localeCorrente, setLocaleCorrente] = useState(null);
  const [pendingCompanySelection, setPendingCompanySelection] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("pending_company_selection")); }
    catch { return null; }
  });

  useEffect(() => {
    let cancelled = false;
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cancelled) return;
          setUser(response.data);
          setLocali(response.data.locali || []);
          
          // Ripristina locale corrente
          const savedLocaleId = localStorage.getItem("localeCorrenteId");
          const userLocali = response.data.locali || [];
          const found = userLocali.find(l => l.id === savedLocaleId);
          const selected = found || (userLocali.length === 1 ? userLocali[0] : null);
          setLocaleCorrente(selected);
          if (selected) localStorage.setItem("localeCorrenteId", selected.id);
          else localStorage.removeItem("localeCorrenteId");
        } catch {
          if (cancelled) return;
          clearStoredToken();
          localStorage.removeItem("localeCorrenteId");
          setToken(null);
          setUser(null);
          setLocali([]);
          setLocaleCorrente(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    verifyToken();
    return () => { cancelled = true; };
  }, [token]);

  // Sliding refresh: rinnova silenziosamente il token quando mancano <2h alla scadenza.
  // Decodifica l'exp dal JWT (payload base64) e schedula un timer.
  useEffect(() => {
    if (!token) return;
    let timer;
    try {
      const payload = JSON.parse(atob(token.split(".")[1] || ""));
      const expMs = (payload?.exp || 0) * 1000;
      const now = Date.now();
      const remaining = expMs - now;
      // Refresh 2h prima della scadenza (o subito se mancano <5 min)
      const delay = Math.max(5000, remaining - 2 * 60 * 60 * 1000);
      timer = setTimeout(async () => {
        try {
          const res = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const newToken = res.data?.access_token;
          if (newToken) {
            setStoredToken(newToken);
            setToken(newToken);  // re-trigger verifyToken effect
          }
        } catch (e) {
          console.warn("[Auth] silent refresh failed:", e?.response?.status);
        }
      }, delay);
    } catch (e) {
      console.warn("[Auth] cannot schedule refresh:", e);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [token]);

  const login = async (username, password) => {
    const device_token = getDeviceToken();
    const response = await axios.post(`${API}/auth/login`, { username: username.toLowerCase().trim(), password, device_token });
    if (response.data.requires_company_selection) {
      const pending = { selectionToken: response.data.selection_token, companies: response.data.companies };
      sessionStorage.setItem("pending_company_selection", JSON.stringify(pending));
      setPendingCompanySelection(pending);
      return { needsSelection: true };
    }
    sessionStorage.removeItem("pending_company_selection");
    setPendingCompanySelection(null);
    const { access_token, user: userData, locali: localiData } = response.data;
    
    setStoredToken(access_token);
    setToken(access_token);
    setUser(userData);
    setLocali(localiData || []);
    const savedId = localStorage.getItem("localeCorrenteId");
    const selected = (localiData || []).find(l => l.id === savedId)
      || (localiData?.length === 1 ? localiData[0] : null);
    setLocaleCorrente(selected);
    if (selected) localStorage.setItem("localeCorrenteId", selected.id);
    else localStorage.removeItem("localeCorrenteId");
    
    return { token: access_token, user: userData, locali: localiData };
  };

  const loadCompanies = useCallback(async () => {
    const res = await axios.get(`${API}/auth/companies`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data.companies || [];
  }, [token]);

  const selectCompany = async (societaId) => {
    const authToken = pendingCompanySelection?.selectionToken || token;
    if (!authToken) throw new Error("Sessione scaduta: accedi di nuovo.");
    const res = await axios.post(`${API}/auth/select-company`, { societa_id: societaId },
      { headers: { Authorization: `Bearer ${authToken}` } });
    sessionStorage.removeItem("pending_company_selection");
    setPendingCompanySelection(null);
    localStorage.removeItem("localeCorrenteId");
    setStoredToken(res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    setLocali(res.data.locali || []);
    const locale = res.data.locali?.length === 1 ? res.data.locali[0] : null;
    setLocaleCorrente(locale);
    if (locale) localStorage.setItem("localeCorrenteId", locale.id);
    return res.data;
  };

  const loginWithToken = (accessToken, userData, localiData) => {
    setStoredToken(accessToken);
    setToken(accessToken);
    setUser(userData);
    setLocali(localiData || []);
    const savedId = localStorage.getItem("localeCorrenteId");
    const selected = (localiData || []).find(l => l.id === savedId)
      || (localiData?.length === 1 ? localiData[0] : null);
    setLocaleCorrente(selected);
    if (selected) localStorage.setItem("localeCorrenteId", selected.id);
    else localStorage.removeItem("localeCorrenteId");
  };

  const logout = () => {
    sessionStorage.removeItem("pending_company_selection");
    setPendingCompanySelection(null);
    clearStoredToken();
    localStorage.removeItem("localeCorrenteId");
    localStorage.removeItem("impersonation_backup_token");
    localStorage.removeItem("impersonation_backup_path");
    setToken(null);
    setUser(null);
    setLocali([]);
    setLocaleCorrente(null);
  };

  /**
   * Inizia una sessione di impersonation come superadmin.
   * - Salva il token superadmin corrente (per poter tornare indietro)
   * - Sostituisce il token utente con quello impersonation
   * - Naviga a "/" (dashboard società target)
   */
  const startImpersonation = (impersonationAccessToken, userData, localiData) => {
    // Backup del token corrente (presumibilmente superadmin) per il return
    const currentToken = localStorage.getItem("token") || localStorage.getItem("superadmin_token");
    if (currentToken) {
      localStorage.setItem("impersonation_backup_token", currentToken);
      localStorage.setItem("impersonation_backup_path", window.location.pathname);
    }
    localStorage.setItem("token", impersonationAccessToken);
    localStorage.removeItem("localeCorrenteId");
    setToken(impersonationAccessToken);
    setUser(userData);
    setLocali(localiData || []);
    if (localiData?.length === 1) {
      setLocaleCorrente(localiData[0]);
      localStorage.setItem("localeCorrenteId", localiData[0].id);
    } else {
      setLocaleCorrente(null);
    }
  };

  /**
   * Termina la sessione di impersonation.
   * Se in tab di impersonation (sessionStorage attivo) → chiude la tab.
   * Altrimenti (legacy path) → ripristina token backup e ricarica.
   */
  const endImpersonation = () => {
    if (isImpersonationTab()) {
      // Tab dedicata: basta chiuderla. La sessione SA originale è intatta
      // nella tab parent (localStorage).
      sessionStorage.clear();
      window.close();
      // Fallback se il browser blocca window.close() (tab non aperta via window.open)
      setTimeout(() => {
        window.location.href = "/superadmin";
      }, 200);
      return;
    }
    // Legacy path (impersonation same-tab via backup token)
    const backupToken = localStorage.getItem("impersonation_backup_token");
    const backupPath = localStorage.getItem("impersonation_backup_path") || "/superadmin";
    localStorage.removeItem("impersonation_backup_token");
    localStorage.removeItem("impersonation_backup_path");
    localStorage.removeItem("token");
    localStorage.removeItem("localeCorrenteId");
    if (backupToken) {
      localStorage.setItem("superadmin_token", backupToken);
    }
    setToken(null);
    setUser(null);
    setLocali([]);
    setLocaleCorrente(null);
    window.location.href = backupPath;
  };

  // Decodifica il claim impersonated_by dal token (se presente)
  const impersonationInfo = (() => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1] || ""));
      if (payload?.impersonation_token) {
        return {
          superadminEmail: payload.impersonated_by || "SuperAdmin",
          expiresAt: payload.exp ? new Date(payload.exp * 1000) : null,
        };
      }
    } catch (e) { /* ignore */ }
    return null;
  })();

  // Interceptor globale: se il backend risponde 401, auto-logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && token) {
          logout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const cambiaLocale = (locale) => {
    setLocaleCorrente(locale);
    localStorage.setItem("localeCorrenteId", locale.id);
  };

  const aggiornaLocali = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocali(response.data.locali || []);
      return response.data.locali;
    } catch (error) {
      console.error("Errore aggiornamento locali:", error);
      return [];
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  // Helper per verificare ruolo
  const isAdmin = user?.ruolo === "admin";
  const isCucina = user?.ruolo === "cucina";
  const isSala = user?.ruolo === "sala";

  // Helper per verificare funzionalità abilitate
  const hasFunzionalita = (func) => {
    if (isAdmin) return true; // Admin ha sempre tutte le funzionalità
    return user?.funzionalita?.includes(func) || false;
  };

  // Verifica se può gestire le ricette (modifica/cancella)
  const canManageRicette = isAdmin || hasFunzionalita("creazione_ricette") || hasFunzionalita("gestione_ricette") || hasFunzionalita("ricette");

  // Verifica se può gestire il menu di vendita (creazione/modifica voci)
  const canManageMenu = isAdmin || hasFunzionalita("gestione_menu");

  // Verifica se deve scegliere locale
  const deveScegliereLocale = locali.length > 1 && !localeCorrente;

  return (
    <AuthContext.Provider value={{ 
      token, 
      loading, 
      login, 
      loginWithToken,
      pendingCompanySelection,
      loadCompanies,
      selectCompany,
      logout, 
      getAuthHeaders,
      user,
      locali,
      localeCorrente,
      cambiaLocale,
      aggiornaLocali,
      isAdmin,
      isCucina,
      isSala,
      hasFunzionalita,
      canManageRicette,
      canManageMenu,
      deveScegliereLocale,
      startImpersonation,
      endImpersonation,
      impersonationInfo,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
